import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MaterialReactTable, { MRT_RowSelectionState } from "material-react-table";
import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";

import { DocumentsQuery } from "api";
import { getDateFormatmonthset, setToLocalStorage, combineDropdownOptions, determineAlgorithmName, isFeatureEnabled, getFromLocalStorage, safeParseJSON } from "utils";
import { useAmplitude, useDocuments, useGetConfigParameter, usePagination, useSitesMultiselect, useTableFilters } from "hooks";
import { LOCAL_STORAGE_KEYS } from "consts";
import {
  ActiveFilters,
  EsgDatePickerField,
  EsgPrimaryLabel,
  InfoMessageBar,
  FLEX_ALIGN_CENTER_GAP_2,
  Loading,
  MarginWrapper,
  Switch,
  Pagination,
  useSwitchFilter,
  useYearMonthPeriodFilter,
  BasicButton,
  InfoLabel,
} from "components";
import { useTableExpandedRows } from "components/react-table";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import { EsgDocumentsUpload } from "components/documents-upload/esg-flow-document-upload";

import { UTILITY_BILL_ALL_DATAENTRIES_VIEW } from "../document-summary-v2/utility-bill/utility-bill-allDataEntries-view/consts";
import ApprovalFlowBar from "./components/approval-flow-bar";
import { filterParamsToQuery } from "./helpers/filterParamsToQuery";
import { ACTIVE_ADDITIONAL_STATUS_DATA_ENTRIES, ACTIVE_FLOW_DATA_ENTRIES, BASE_DATA_ENTRIES, getDataUploadColumns, ADD_DOCUMENT_BILL_PAGE } from "./consts";
import { createDocumentUploadFilterConfig, DocumentUploadFilters } from "./filter-config";
import { DataEntriesStatusType } from "./types";
import { EsgActions } from "./components/table";

export const DocumentUploadPage = () => {
  // ---- Hooks
  const navigate = useNavigate();
  const currentUrl = useLocation();
  const { data: configParameter } = useGetConfigParameter();
  const { trackEvent } = useAmplitude();

  // ---- Const ---- 
  const fromDateInitial = dayjs().startOf("month").format("YYYY-MM-DD");
  const toDateInitial = dayjs().add(1, "day").format("YYYY-MM-DD");

  // Local Storage
  const storedFromDate = localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_FROM_DATE);
  const storedToDate = localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_TO_DATE);
  const rawDataEntriesStatus = localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_DATA_ENTRIES_STATUS_FILTER);
  const storedDataEntriesStatus = useMemo(() => rawDataEntriesStatus ? JSON.parse(rawDataEntriesStatus) : [], [rawDataEntriesStatus]);
  const storedSitesFilter = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_SITES_FILTER) || "[]");
  const storedSiteIdsFilter = storedSitesFilter.map((site: any) => site.value);
  const viewedDocumentID = getFromLocalStorage("viewedDocumentID");

  // ---- States
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const rowSelectionLength = Object.keys(rowSelection).length > 0;

  const isApprovalFlowValueOn = isFeatureEnabled(configParameter, "second_layer_approval_feature");
  const isAdditionalStatusesOn = isFeatureEnabled(configParameter, "unusable_status_enabled");

  // Right below 👇 we have basicStatuses always on, that means = true;
  const dropDownStatusOption: Array<{ label: string, name: string, value: number }> = combineDropdownOptions(
    [true, isApprovalFlowValueOn, isAdditionalStatusesOn],
    [BASE_DATA_ENTRIES, ACTIVE_FLOW_DATA_ENTRIES, ACTIVE_ADDITIONAL_STATUS_DATA_ENTRIES],
  );

  const [dateError, setDateError] = useState(false);

  const initialFromDate = storedFromDate || fromDateInitial;
  const initialToDate = storedToDate || toDateInitial;

  const [fromDate, setFromDate] = useState<string>(initialFromDate);
  const [toDate, setToDate] = useState<string>(initialToDate);
  const [dataEntriesStatus, setDataEntriesStatus] = useState<any>([]);

  // ---- API's
  const { setInitialExpanded } = useTableExpandedRows();
  const updateExternalStates = useCallback(() => { setInitialExpanded() }, [setInitialExpanded]);
  const { availableYears } = useYearMonthPeriodFilter({ updateExternalStates });

  const minDate = dayjs(`${Math.min(...(availableYears || [dayjs().utc().year()]))}-01-01`).utc();

  const { siteIds, isSitesLoading, selectedSites, setSelectedSites, sitesOptions } = useSitesMultiselect({ updateExternalStates });

  const handleSetSites = (param: any) => {
    setSelectedSites(param);
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_SITES_FILTER, JSON.stringify(param));
  }

  const handleSetDataEntriesStatus = (param: any) => {
    setDataEntriesStatus(param);
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_DATA_ENTRIES_STATUS_FILTER, JSON.stringify(param));
  }

  const { value: isUnmoved, onSwitchChange: onIsUnmovedChange } = useSwitchFilter({ key: "isUnmoved", updateExternalStates });

  // ---- Pagination (using unified hook)
  const {
    pagination,
    paginatedQuery,
    onPageChange,
    onSizeChange,
    updateFromApiResponse,
    uiPage,
  } = usePagination({
    localStorageKey: LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_PAGINATION,
    initialPage: 0,
    initialSize: 50,
  });

  // ---- Column filters (same behavior as Site Management: apply/clear workflow, localStorage persistence)
  const filterConfig = useMemo(() => createDocumentUploadFilterConfig(), []);
  const tableFilters = useTableFilters<DocumentUploadFilters>({
    config: filterConfig,
    pagination: { onPageChange, resetPage: () => onPageChange(1) },
  });
  const mrtFilterProps = tableFilters.getMRTFilterProps();
  const activeFiltersProps = tableFilters.getActiveFiltersProps();

  const onPageChangeHandler = (newPage: number) => {
    onPageChange(newPage);
  };

  const onPageSizeChangeHandler = (newPageSize: number) => {
    onSizeChange(newPageSize);
  };

  const searchQuery = useMemo(
    () => ({
      uploadDateFrom: !dateError
        ? (typeof fromDate === "string" ? fromDate : dayjs(fromDate).format("YYYY-MM-DD"))
        : "",
      uploadDateTo: !dateError
        ? (typeof toDate === "string" ? toDate : dayjs(toDate).format("YYYY-MM-DD"))
        : "",
      siteIds: siteIds.length > 0 ? siteIds : storedSiteIdsFilter,
      status: (dataEntriesStatus.length > 0 ? dataEntriesStatus : storedDataEntriesStatus).map((item: any) => item.label).join(","),
      isUnmoved,
      ...paginatedQuery,
    }),
    [
      dateError,
      fromDate,
      toDate,
      siteIds,
      storedSiteIdsFilter,
      dataEntriesStatus,
      storedDataEntriesStatus,
      isUnmoved,
      paginatedQuery
    ],
  );

  const query = useMemo<DocumentsQuery>(
    () => ({
      ...searchQuery,
      ...filterParamsToQuery(tableFilters.getQueryParams() as Record<string, any>),
    }),
    [searchQuery, tableFilters]
  );

  const [prevQuery, setPrevQuery] = useState(query);

  // State to track previous dataDocuments for comparison
  const [prevDataDocuments, setPrevDataDocuments] = useState<any>(null);

  const { data: dataDocuments, isLoading } = useDocuments(query);

  const handleSearch = () => {
    tableFilters.applyFilters();
    onPageChange(1);
  };

  const tableRows = dataDocuments?.documents || [];

  // Page Handler Functions
  const handleRowSelection = () => {
    setRowSelection({});
  };

  const handleFromDateChange = (date: any) => {
    const day = dayjs(date);
    if (!day.isValid()) {
      return;
    }

    const dateValue = day.startOf("month").format("YYYY-MM-DD");
    const currentToDate = typeof toDate === "string" ? toDate : dayjs(toDate).format("YYYY-MM-DD");

    setFromDate(dateValue);
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_FROM_DATE, dateValue);
    setDateError(dateValue > currentToDate);
  };

  const handleToDateChange = (date: any) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    const selectedDate = new Date(date);
    if (Number.isNaN(selectedDate.getTime())) {
      return;
    }

    const selectedMonth = selectedDate.getMonth();
    const selectedYear = selectedDate.getFullYear();

    let toDateValue: string;

    if (currentMonth === selectedMonth && currentYear === selectedYear) {
      toDateValue = dayjs(today).add(1, "day").format("YYYY-MM-DD");
    } else {
      const lastDateOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
      toDateValue = dayjs(lastDateOfMonth).format("YYYY-MM-DD");
    }

    const currentFromDate = typeof fromDate === 'string' ? fromDate : dayjs(fromDate).format("YYYY-MM-DD");
    if (toDateValue < currentFromDate) {
      setDateError(true);
      return;
    }

    setToDate(toDateValue);
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_TO_DATE, toDateValue);
    setDateError(false);
  };

  const handleResetDate = () => {
    setFromDate(fromDateInitial);
    setToDate(toDateInitial);
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_FROM_DATE, fromDateInitial);
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_TO_DATE, toDateInitial);
  };

  const handleClearFilters = () => {
    handleResetDate();
    setDateError(false);
    setSelectedSites([]);
    setDataEntriesStatus([]);
    tableFilters.clearFilters();
    onPageChange(1);
    onIsUnmovedChange(false);

    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_SITES_FILTER, JSON.stringify([]));
    localStorage.setItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_DATA_ENTRIES_STATUS_FILTER, JSON.stringify([]));
  };

  const setDateValue = (dateValue: string | null) => dateValue ? dayjs(dateValue) : null;

  const getCircularReplacer = (): any => {
    const seen = new WeakSet();
    return (key: any, value: any) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) {
          return undefined;
        }
        seen.add(value);
      }
      return value;
    };
  };

  const handleRowClick = (row: any, samePage?: boolean) => {
    const actualRow = samePage ? row?.row : row;
    setToLocalStorage("dataEntries", JSON.stringify(actualRow, getCircularReplacer()));
    setToLocalStorage("type", "Upload");
    setToLocalStorage("Upload_URL", (currentUrl?.pathname || '') + (currentUrl?.search || ''));
    setToLocalStorage("algorithm", determineAlgorithmName(actualRow.original));
    setToLocalStorage("viewedDocumentID", actualRow.original?.id as string);
    if (actualRow.original?.bills !== null) {
      if (actualRow.original?.bills?.length > 0) {
        setToLocalStorage("summaryDocumentID", actualRow.original?.bills[0]?.id);
        if (samePage) {
          navigate(UTILITY_BILL_ALL_DATAENTRIES_VIEW.ABSOLUTE_PATH(actualRow.original?.bills[0]?.id));
        }
      } else {
        setToLocalStorage("summaryDocumentID", actualRow.original?.id);
        if (samePage) {
          navigate(ADD_DOCUMENT_BILL_PAGE.ABSOLUTE_PATH(actualRow.original?.id as string));
        }
      }
    }
  };

  const getMuiTableBodyRowProps = (row: any) => ({
    onClick: () => handleRowClick(row, true),
    sx: {
      cursor: "pointer",
      backgroundColor:
        String(row?.row?.original?.id) === String(viewedDocumentID)
          ? "#DCDCDC"
          : "",
    },
  });

  const dataUploadColumns = getDataUploadColumns({ handleRowClick });

  const handleTrackEvents = () => {
    // Get new documents by comparing current with previous and filter only new documents (documents that weren't in the previous state)
    const previousDocumentIds = prevDataDocuments?.documents?.map((doc: any) => doc.id) || [];
    const newDocuments = dataDocuments.documents?.filter((doc: any) => !previousDocumentIds.includes(doc.id)) || [];

    // Track warning events only for new documents that have warnings
    const newDocumentsWithWarnings = newDocuments.filter((doc: any) => doc.dateEntryStatus?.includes("Warning"));
    newDocumentsWithWarnings.forEach(() => { trackEvent("AI Processing Warning", "Documents - Upload") });

    // Check for new successfully parsed documents (status is PARSED and no warnings) and track success events only for new successfully parsed documents
    const newSuccessfullyParsedDocuments = newDocuments.filter((doc: any) => doc.status === "PARSED" && doc.dateEntryStatus?.includes("Emissions Included"));
    newSuccessfullyParsedDocuments.forEach(() => { trackEvent("AI Processing Emissions Included", "Documents - Upload") });

    // Check for new signed of documents (status is PARSED and dateEntryStatus includes "Signed Off") and track success events only for new signed of documents
    const newSignedOfDocuments = newDocuments.filter((doc: any) => doc.status === "PARSED" && doc.dateEntryStatus?.includes("Signed Off"));
    newSignedOfDocuments.forEach(() => { trackEvent("Approved Signed Off", "Documents - Upload") });

    // Check for new locked documents (status is PARSED and dateEntryStatus includes "Locked") and track success events only for new locked documents
    const newLockedDocuments = newDocuments.filter((doc: any) => doc.status === "PARSED" && doc.dateEntryStatus?.includes("Locked"));
    newLockedDocuments.forEach(() => { trackEvent("Approved Locked", "Documents - Upload") });
  };

  useEffect(() => {
    localStorage.removeItem("billEntriesIdSelected");

    const currentStoredSitesFilter = safeParseJSON(
      localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_SITES_FILTER),
      [],
    );
    const currentStoredDataEntriesStatus = safeParseJSON(
      localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_DATA_ENTRIES_STATUS_FILTER),
      [],
    );

    // Always set from localStorage (even if empty) to ensure URL params are cleared when localStorage is empty
    setSelectedSites(currentStoredSitesFilter ?? []);
    setDataEntriesStatus(currentStoredDataEntriesStatus ?? []);

    if (storedFromDate && storedToDate) {
      const fromDateValue = dayjs(storedFromDate).format("YYYY-MM-DD");
      const toDateValue = dayjs(storedToDate).format("YYYY-MM-DD");
      if (fromDateValue > toDateValue) {
        setDateError(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure localStorage takes precedence over URL params (runs after hook reads from URL)
  useEffect(() => {
    if (!isSitesLoading) {
      const currentStoredSitesFilter = safeParseJSON(
        localStorage.getItem(LOCAL_STORAGE_KEYS.DOCUMENT_UPLOAD_PAGE_SITES_FILTER),
        [],
      );
      // If localStorage is empty but sites are selected (from URL), clear them
      if ((!currentStoredSitesFilter || currentStoredSitesFilter.length === 0) && selectedSites.length > 0) {
        setSelectedSites([]);
      }
    }
  }, [isSitesLoading, setSelectedSites, selectedSites]);

  useEffect(() => {
    const isDifferent = JSON.stringify(searchQuery) !== JSON.stringify(prevQuery);
    if (isDifferent) {
      setPrevQuery(query); // Update previous state if changed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (dataDocuments && dataDocuments?.pageResult) {
      handleTrackEvents();
      setPrevDataDocuments(dataDocuments);
      updateFromApiResponse(dataDocuments.pageResult);
    }
  }, [dataDocuments, prevDataDocuments, updateFromApiResponse]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <MarginWrapper>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 3,
        flexWrap: "wrap",
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          gap: 2,
        }}>
          <Box sx={{
            maxWidth: '75%',
            "@media (max-width: 1323px)": {
              maxWidth: '60%',
            },
          }}>
            <Box sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}>
              <Stack direction="row" spacing={2}>
                <EsgDatePickerField
                  label={<InfoLabel
                    title="Start Month"
                    placement="top"
                    tooltipSx={{
                      maxWidth: "180px",
                      height: "fit-content",
                      fontSize: "10px",
                      textAlign: "center",
                    }}
                    labelSx={{
                      width: "100%",
                      height: "18px",
                      padding: "0px",
                      marginBottom: "4px",
                      flexDirection: "row-reverse",
                      justifyContent: "flex-end",
                      backgroundColor: "transparent",
                      "& > svg": {
                        width: "14px",
                        height: "14px",
                        color: "black",
                      }
                    }}
                    titleSx={{
                      color: "black",
                      fontSize: "12px",
                    }}
                    infoMessage="Filter to documents uploaded within the date range starting from this month"
                  />}
                  views={["year", "month"]}
                  openTo="month"
                  minDate={minDate}
                  format={getDateFormatmonthset()}
                  disableFuture
                  disableHighlightToday
                  onAccept={handleFromDateChange}
                  onChange={handleFromDateChange}
                  setDate={handleFromDateChange}
                  value={setDateValue(fromDate)}
                  placeholdervalue="MM-YYYY"
                  filterAll={false}
                  width="140px"
                />
                <EsgDatePickerField
                  label={<InfoLabel
                    title="End Month"
                    placement="top"
                    tooltipSx={{
                      maxWidth: "180px",
                      height: "fit-content",
                      fontSize: "10px",
                      textAlign: "center",
                    }}
                    labelSx={{
                      width: "100%",
                      height: "18px",
                      padding: "0px",
                      marginBottom: "4px",
                      flexDirection: "row-reverse",
                      justifyContent: "flex-end",
                      backgroundColor: "transparent",
                      "& > svg": {
                        width: "14px",
                        height: "14px",
                        color: "black",
                      }
                    }}
                    titleSx={{
                      color: "black",
                      fontSize: "12px",
                    }}
                    infoMessage="Filter to documents uploaded within the date range ending in this month"
                  />}
                  width="140px"
                  views={["year", "month"]}
                  minDate={minDate}
                  openTo="month"
                  format={getDateFormatmonthset()}
                  disableHighlightToday
                  onAccept={handleToDateChange}
                  onChange={handleToDateChange}
                  setDate={handleToDateChange}
                  value={setDateValue(toDate)}
                  placeholdervalue="MM-YYYY"
                  filterAll={false}
                />
              </Stack>

              <Box sx={{ minWidth: "210px", width: "fit-content", paddingLeft: "0px" }}>
                <EsgMultiselectControlledField
                  label="Sites"
                  disabled={isSitesLoading}
                  selectedValues={selectedSites}
                  setSelectedValues={handleSetSites}
                  options={sitesOptions}
                  isAllSelectedOnEmptySelection
                  type="rounded"
                />
              </Box>

              <Box sx={{ minWidth: "210px", width: "fit-content" }}>
                <EsgMultiselectControlledField
                  label="Data Entries Status"
                  disabled={isSitesLoading}
                  selectedValues={dataEntriesStatus}
                  setSelectedValues={handleSetDataEntriesStatus}
                  options={dropDownStatusOption}
                  isAllSelectedOnEmptySelection
                  type="rounded"
                />
              </Box>

              <Box sx={{
                paddingTop: "20px",
                maxWidth: {
                  xl: "125px",
                },
                "@media (min-width: 1600px)": {
                  maxWidth: "200px",
                }
              }}>
                <Switch
                  isChecked={isUnmoved}
                  label="Show only unmoved"
                  onChange={onIsUnmovedChange}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  pt: "20px",
                }}
              >
                <BasicButton variant="contained" sx={{ width: "120px", mt: "0px" }} disabled={dateError} onClick={handleSearch}>Search</BasicButton>
                <BasicButton variant="outlined" sx={{ width: "120px", mt: "0px" }} onClick={handleClearFilters}>Clear Filters</BasicButton>
              </Box>
            </Box>

            <Box sx={[{ mt: '36px', width: '100%' }]}>
              <Box sx={[FLEX_ALIGN_CENTER_GAP_2]}>
                <EsgDocumentsUpload isLoading={isLoading} onUploadCompleted={handleClearFilters} />
                {rowSelectionLength && <EsgActions
                  rowSelection={rowSelection}
                  setrowSelection={handleRowSelection}
                />}
              </Box>
            </Box>

          </Box>
          <Box>
            <ApprovalFlowBar data={dataDocuments as DataEntriesStatusType} />
          </Box>
        </Box>
        {dateError && (
          <Box sx={[FLEX_ALIGN_CENTER_GAP_2, { mt: 1 }]}>
            <EsgPrimaryLabel sx={{ color: "red" }}>
              Invalid date selection. Please choose a month after the From Month.
            </EsgPrimaryLabel>
          </Box>
        )}
      </Box>

      {isLoading || !dataDocuments ? (
        <Loading />
      ) : (
        <>
          {dataDocuments?.documents.length === 0 && (
            <InfoMessageBar>
              No bills have been uploaded for this month yet. Please check the previous month&apos;s bills or upload more bills.
            </InfoMessageBar>)}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              marginTop: "20px",
            }}
          >
            <ActiveFilters
              appliedFilters={activeFiltersProps.appliedFilters}
              onClearFilter={(filterId) => activeFiltersProps.onClearFilter(filterId)}
              displayNameMap={activeFiltersProps.displayNameMap}
            />
          </Box>
          {tableRows &&
            <>
              <MaterialReactTable
                enableTopToolbar
                enableStickyHeader
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                data={dataDocuments?.documents || []}
                columns={dataUploadColumns}
                enableRowSelection
                enablePagination
                manualPagination
                rowCount={pagination.size}
                onRowSelectionChange={setRowSelection}
                enableColumnFilters={mrtFilterProps.enableColumnFilters}
                manualFiltering={mrtFilterProps.manualFiltering}
                onColumnFiltersChange={mrtFilterProps.onColumnFiltersChange}
                muiTableHeadCellFilterTextFieldProps={mrtFilterProps.muiTableHeadCellFilterTextFieldProps}
                initialState={{ density: "compact", columnFilters: mrtFilterProps.initialState.columnFilters }}
                state={{ rowSelection, globalFilter: '' }}
                getRowId={(row: any) => row.id}
                muiTableContainerProps={{
                  sx: {
                    "& thead tr:first-of-type th:first-of-type": {
                      paddingLeft: "20px",
                    },
                    "& tbody tr": {
                      "& td:first-of-type": {
                        width: "20px",
                        minWidth: "20px",
                        maxWidth: "20px",
                        paddingLeft: "20px",
                        paddingRight: "0px",
                      },
                      "& td:nth-of-type(2)": {
                        width: "20px",
                        minWidth: "20px",
                        maxWidth: "20px",
                        paddingLeft: "0px",
                        paddingRight: "25px",
                      }
                    },
                    "& tbody tr:first-of-type td > p": {
                      display: "flex",
                      width: "100%",
                      justifyContent: "center",
                    },
                    height: "calc(100vh - 435px)",
                    minHeight: "200px",
                    border: "1px solid #D9D9D9",
                    borderRadius: "10px",
                    minWidth: "100%",
                    width: "100%",
                    maxWidth: "100%",
                  },
                }}
                muiTablePaperProps={{
                  sx: {
                    borderRadius: "10px",
                    boxShadow: "none",
                  },
                }}
                muiTopToolbarProps={{
                  sx: {
                    '& .MuiIconButton-root': {
                      display: 'none',
                    },
                    backgroundColor: "white",
                    display: Object.keys(rowSelection).length > 0 ? 'block' : 'none'
                  },
                }}
                muiBottomToolbarProps={{
                  sx: {
                    display: "none",
                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    lineHeight: "30px",
                    "& .MuiBadge-root": {
                      display: "none",
                    },
                  },
                }}
                muiTableHeadRowProps={{
                  sx: {
                    "& > th": {
                      paddingTop: 1,
                      paddingBottom: 1,
                    },
                    backgroundColor: "#F8FAFC",
                  },
                }}
                muiTableBodyRowProps={(row: any) => getMuiTableBodyRowProps(row)}
                muiTableBodyCellProps={(row) => ({
                  sx: {
                    opacity: row.row.original.moved ||
                      row.row.original.status === "PARSED" ||
                      row.row.original.status === "NO_RESPONSE"
                      ? 1
                      : 0.5,
                    ":disabled":
                      !(
                        row.row.original.moved ||
                        row.row.original.status === "PARSED" ||
                        row.row.original.status === "NO_RESPONSE"
                      ),
                  },
                })}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "10px",
                }}
              >
                <Pagination
                  page={uiPage}
                  rowsCount={pagination.totalRows}
                  rowsPerPage={pagination.size}
                  onPageChange={onPageChangeHandler}
                  onRowsPerPageChange={onPageSizeChangeHandler}
                />
              </Box>
            </>
          }
        </>
      )}
    </MarginWrapper>
  );
};